import { useState, type FormEvent } from 'react';
import { Alert, Box, Button, Link as MuiLink, Paper, TextField, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import { CLIENT_BRAND_NAME } from '../branding';
import { legalDocuments } from '../legal/legalDocuments';
import { CORE_CREDENTIAL_REJECTION_MESSAGE, InvalidCredentialsError } from './authApi';
import type { DashboardAuth } from './authStore';

export type LoginViewProps = {
  login: (credentials: { email: string; password: string }) => Promise<DashboardAuth>;
  onSuccess: (auth: DashboardAuth) => void;
};

type LoginStatus = 'idle' | 'submitting' | 'error';

const describeLoginError = (error: unknown): string =>
  error instanceof InvalidCredentialsError
    ? 'Check your credentials and try again.'
    : 'Could not reach the server. Check your connection and try again.';

export const LoginView = ({ login, onSuccess }: LoginViewProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<LoginStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const isSubmitting = status === 'submitting';

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const credentials = { email: email.trim(), password: password.trim() };
    if (!credentials.email || !credentials.password) {
      setStatus('error');
      setErrorMessage(CORE_CREDENTIAL_REJECTION_MESSAGE);
      return;
    }

    setStatus('submitting');

    login(credentials)
      .then(auth => {
        setStatus('idle');
        onSuccess(auth);
      })
      .catch((error: unknown) => {
        setStatus('error');
        setErrorMessage(describeLoginError(error));
      });
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
      <Paper component="form" onSubmit={handleSubmit} sx={{ p: 4, width: '100%', maxWidth: 400 }}>
        <Typography variant="h4" gutterBottom>
          {CLIENT_BRAND_NAME}
        </Typography>
        {status === 'error' && (
          <Alert severity="error" data-testid="login-error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={event => setEmail(event.target.value)}
          required
          fullWidth
          margin="normal"
          autoComplete="username"
          disabled={isSubmitting}
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={event => setPassword(event.target.value)}
          required
          fullWidth
          margin="normal"
          autoComplete="current-password"
          disabled={isSubmitting}
        />
        <Button type="submit" variant="contained" fullWidth disabled={isSubmitting} sx={{ mt: 2 }}>
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </Button>
        <Box component="nav" aria-label="Legal documents" sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
          {legalDocuments.map(document => (
            <MuiLink key={document.path} component={Link} to={document.path} variant="body2" underline="hover">
              {document.label}
            </MuiLink>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};
